import { createAsyncThunk } from '@reduxjs/toolkit';
import HttpError from '@common/interfaces/api/http-error';
import ValidationResult from '@common/interfaces/api/validation-result';
import fleetManagementDataService from '../../services/data/fleet-management-data-service';
import {
  returnAxiosOrGeneralFetchError,
  returnAxiosValidationOrGeneralFetchError,
} from '../../services/data/axios-base';
import FleetDashboard from 'models/fleet-management/FleetDashboard';
import DeepPartial from '@common/utils/deep-partial';
import FleetDataConfiguration from 'models/fleet-management/FleetDataConfiguration';
import FleetDataSet, { FleetDataRows } from 'models/fleet-management/FleetDataSet';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import type { RootState } from 'store';
import type { FleetDataModel } from 'models/enums/fleet-management';
import {
  FleetDashboardFilters,
  type FleetDataFilter,
} from 'models/fleet-management/FleetDataFilter';
import { RobotModel, RobotScope } from 'components/fleet-management/FleetFiltersBar';

export const getFleetDashboards = createAsyncThunk<
  FleetDashboard[],
  void,
  { rejectValue: HttpError }
>('fleet/dashboards/list', async (_, { rejectWithValue }) => {
  try {
    return await fleetManagementDataService.getFleetDashboards();
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const createNewFleetDashboard = createAsyncThunk<
  FleetDashboard,
  DeepPartial<FleetDashboard>,
  { rejectValue: HttpError }
>('fleet/dashboards/post', async (payload, { rejectWithValue }) => {
  try {
    return await fleetManagementDataService.postNewFleetDashboard(payload);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const saveFleetDashboard = createAsyncThunk<
  FleetDashboard,
  FleetDashboard,
  { rejectValue: HttpError }
>('fleet/dashboards/patch', async (payload, { rejectWithValue }) => {
  try {
    return await fleetManagementDataService.patchFleetDashboard(payload);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const deleteDashboardById = createAsyncThunk<string, string, { rejectValue: HttpError }>(
  'fleet/dashboards/delete',
  async (payload, { rejectWithValue }) => {
    try {
      return await fleetManagementDataService.deleteFleetDashboardById(payload);
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);

export const getFleetData = createAsyncThunk<
  { loadTime: number; data: any },
  string,
  { rejectValue: HttpError | ValidationResult[] }
>('fleet/post-fetch-data', async (datasetId, { rejectWithValue }) => {
  const start = performance.now();
  try {
    const data = await fleetManagementDataService.getFleetData(datasetId);
    const end = performance.now();
    return { loadTime: end - start, data };
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

/** How long an answer to a live query is reused before asking again. */
export const LIVE_DATA_MAX_AGE_MS = 5 * 60 * 1000;
/** How many live query answers are kept before the oldest are dropped. */
export const LIVE_QUERY_CACHE_LIMIT = 40;

const LIVE_QUERY_KEY_PREFIX = 'query:';

export interface LiveQuery {
  model: FleetDataModel;
  start: string;
  end: string;
  filters: FleetDataFilter[];
  /** Left empty for the model's default fields. */
  fields?: string[];
}

/**
 * The store key of a live query: the question itself. Filter ids are left out, since the filters
 * are rebuilt with fresh ids on most renders and a saved dashboard carries its own.
 */
export const liveQueryKey = ({ model, start, end, filters, fields }: LiveQuery) =>
  LIVE_QUERY_KEY_PREFIX +
  JSON.stringify({
    model,
    start,
    end,
    fields: fields ?? [],
    filters: filters.map(({ field, type, values }) => ({ field, type, values })),
  });

export const isLiveQueryKey = (key: string) => key.startsWith(LIVE_QUERY_KEY_PREFIX);

/**
 * A key already loading or recently answered needs no second request: every page and section
 * asking the same question shares one answer.
 */
const needsFetch = (key: string, state: RootState) => {
  const entry = state.fleetManagement.live.find((l) => l.id === key);
  if (entry == null) return true;
  if (entry.rest.getStatus === HttpFetchStatus.Loading) return false;
  if (entry.rest.getStatus !== HttpFetchStatus.Success || entry.fetchedAt == null) return true;
  return Date.now() - entry.fetchedAt > LIVE_DATA_MAX_AGE_MS;
};

export const getFleetDataByFilters = createAsyncThunk<
  {
    loadTime: number;
    data: FleetDataRows;
    datasetIds: string;
    payloadBytes: number | null;
  },
  { datasetIds: string; payload: FleetDataSet },
  { rejectValue: HttpError | ValidationResult[]; state: RootState }
>(
  'fleet/post-fetch-data-by-filters',
  async ({ datasetIds, payload }, { rejectWithValue }) => {
    const start = performance.now();
    try {
      const { data, payloadBytes } =
        await fleetManagementDataService.getFleetDataByFilters(payload);
      return { loadTime: performance.now() - start, data, datasetIds, payloadBytes };
    } catch (error) {
      return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
    }
  },
  { condition: ({ datasetIds }, { getState }) => needsFetch(datasetIds, getState()) },
);

/** The filter bar's selections, as the health and performance endpoints read them. */
export interface InsightsQuery {
  start: string;
  end: string;
  dealerIds: string[];
  customerIds: string[];
  tags: string[];
  countries: string[];
  model: RobotModel | null;
  robotScope: RobotScope | null;
}

export const toInsightsQuery = (filters: FleetDashboardFilters): InsightsQuery => ({
  start: filters.start,
  end: filters.end,
  dealerIds: filters.dealers,
  customerIds: filters.dealersCustomers,
  tags: filters.tags,
  countries: filters.countries,
  model: filters.model === RobotModel.ALL ? null : filters.model,
  robotScope: filters.robotScope === RobotScope.ALL ? filters.robotScope : null,
});

/** The store key of an insights request, in the same key space as the live dataset queries. */
export const insightsKey = (path: string, query: InsightsQuery) =>
  LIVE_QUERY_KEY_PREFIX + JSON.stringify({ path, query });

export const getInsights = createAsyncThunk<
  { loadTime: number; data: unknown; key: string; payloadBytes: number | null },
  { key: string; path: string; query: InsightsQuery },
  { rejectValue: HttpError | ValidationResult[]; state: RootState }
>(
  'fleet/get-insights',
  async ({ key, path, query }, { rejectWithValue }) => {
    const start = performance.now();
    try {
      const { data, payloadBytes } = await fleetManagementDataService.getInsights(path, query);
      return { loadTime: performance.now() - start, data, key, payloadBytes };
    } catch (error) {
      return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
    }
  },
  { condition: ({ key }, { getState }) => needsFetch(key, getState()) },
);

export const getFleetDataSets = createAsyncThunk<
  FleetDataConfiguration,
  void,
  { rejectValue: HttpError }
>('fleet/datasets/list', async (_, { rejectWithValue }) => {
  try {
    return await fleetManagementDataService.getFleetDataSets();
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const createNewFleetDataSet = createAsyncThunk<
  FleetDataSet,
  DeepPartial<FleetDataSet>,
  { rejectValue: HttpError | ValidationResult[] }
>('fleet/datasets/post', async (payload, { rejectWithValue }) => {
  try {
    return await fleetManagementDataService.postNewFleetDataSet(payload);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const saveFleetDataSet = createAsyncThunk<
  FleetDataSet,
  FleetDataSet,
  { rejectValue: HttpError | ValidationResult[] }
>('fleet/datasets/patch', async (payload, { rejectWithValue }) => {
  try {
    return await fleetManagementDataService.patchFleetDataSet(payload);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const deleteDataSetById = createAsyncThunk<string, string, { rejectValue: HttpError }>(
  'fleet/datasets/delete',
  async (payload, { rejectWithValue }) => {
    try {
      return await fleetManagementDataService.deleteFleetDataSetById(payload);
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);
